import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { CheckCircle2, Clock } from "@/components/icons";
import StatCard from "../../components/StatCard";
import {
  isInCurrentWeek,
  weekStart,
  weekEnd,
  formatWeekRange,
} from "../../lib/weekCutoff";

export default function PharmacySales() {
  const [sales, setSales] = useState([]);

  useEffect(() => {
    api.get("/pharmacy/sales").then((r) => setSales(r.data));
    // Re-render diario por si el usuario deja la pestaña abierta al cruzar
    // el corte de jueves→viernes.
    const t = setInterval(() => setSales((s) => [...s]), 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const now = new Date();
  const currentWeek = sales.filter((s) => isInCurrentWeek(s.created_at, now));
  const totals = currentWeek.reduce(
    (a, s) => {
      a.tot += s.total_amount;
      a.com += s.platform_commission_amount;
      a.pend += s.pharmacy_paid ? 0 : s.platform_commission_amount;
      return a;
    },
    { tot: 0, com: 0, pend: 0 },
  );

  // Formato del cierre: "cierra JUE 10 jul 23:59"
  const closeAt = weekEnd(now);
  const closeLabel = closeAt.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div className="space-y-6" data-testid="pharmacy-sales">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#163144] tracking-tight">
            Ventas y comisión — semana en curso
          </h1>
          <p className="text-sm text-[#1B405B]/70">
            Semana operativa <b>{formatWeekRange(weekStart(now))}</b>. Corte
            automático al final del jueves ({closeLabel} 23:59). Los totales
            vuelven a cero al iniciar el viernes.
          </p>
        </div>
        <Link
          to="/farmacia/historial"
          data-testid="link-history"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-white border border-black/[0.08] hover:bg-black/[0.03] text-[#163144]"
        >
          Ver historial semanal →
        </Link>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 pt-2">
        <StatCard
          title="Total vendido esta semana"
          value={`$${totals.tot.toFixed(2)}`}
          subtitle={`${currentWeek.length} venta${currentWeek.length === 1 ? "" : "s"}`}
          testid="stat-total-sold"
        />
        <StatCard
          title="Comisión plataforma"
          value={`$${totals.com.toFixed(2)}`}
          subtitle="Generada esta semana"
          testid="stat-commission"
        />
        <StatCard
          title="Pendiente pagar"
          value={`$${totals.pend.toFixed(2)}`}
          subtitle="A la plataforma"
          active={totals.pend > 0}
          testid="stat-pending-pay"
        />
      </div>

      <div className="bg-white rounded-[28px] overflow-x-auto border border-black/[0.06]">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="text-left">
            <tr className="text-[11px] uppercase tracking-widest text-[#1B405B]/70 border-b border-[#1B405B]/10">
              <th className="px-5 py-4">Fecha</th>
              <th className="px-5 py-4">Receta</th>
              <th className="px-5 py-4 text-right">Venta</th>
              <th className="px-5 py-4 text-right">
                Comisión ({currentWeek[0]?.platform_commission_pct ?? 10}%)
              </th>
              <th className="px-5 py-4">Estado</th>
            </tr>
          </thead>
          <tbody>
            {currentWeek.map((s) => (
              <tr
                key={s.id}
                className="border-b border-[#1B405B]/8 hover:bg-black/[0.02]"
              >
                <td className="px-5 py-3">
                  {new Date(s.created_at).toLocaleDateString("es-ES", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                </td>
                <td className="px-5 py-3 font-bold text-[#163144]">
                  {s.prescription_id.slice(0, 8).toUpperCase()}
                </td>
                <td className="px-5 py-3 text-right">
                  ${s.total_amount.toFixed(2)}
                </td>
                <td className="px-5 py-3 text-right font-bold text-[#1B405B]">
                  ${s.platform_commission_amount.toFixed(2)}
                </td>
                <td className="px-5 py-3">
                  {s.pharmacy_paid ? (
                    <span className="inline-flex items-center gap-1 text-[#019A75] text-xs font-bold">
                      <CheckCircle2 className="w-4 h-4" /> Pagada
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-bold">
                      <Clock className="w-4 h-4" /> Pendiente
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {currentWeek.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-[#1B405B]/60 py-10">
                  No hay ventas registradas en esta semana.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
